import React, { Dispatch, SetStateAction } from 'react';

import FormControl from 'components/FormControl';
import { I18N_COMPANY_LABEL_PATH } from 'components/CompanyProfile/constants';
import TextInput from 'components/Inputs/TextInput';
import Button from 'components/Button/new';
import {
  I18N_PLATFORM_COMMON_WORD_PATH,
  I18N_PLATFORM_ROLES_COMPANY_LABEL_PATH,
} from 'constants/i18n';
import { ICompany } from 'interfaces/ICompany';
import { translateText } from 'utils/i18n';

import { IFieldsErrors } from './useValidateCompany';
import CompanyLogo from './CompanyLogo';
import { CompanyFieldsNames } from './constants';
import styles from './CompanyForm.module.scss';
import formsStyles from '../FormSection.module.scss';

interface Props {
  company: ICompany;
  fieldsErrors: IFieldsErrors | null;
  setFieldsErrors: Dispatch<SetStateAction<IFieldsErrors | null>>;
  onChange: (field: string, value: any) => void;
  showCompanyAliasesButton?: boolean;
  setCompanyLogoRemoved?: (removed: boolean) => void;
  setCompanyLogoToUpload?: (file?: File) => void;
  onOpenAliases?: () => void;
}
export const CompanyBasicInfoSection = ({
  company,
  fieldsErrors,
  setFieldsErrors,
  onChange,
  showCompanyAliasesButton,
  setCompanyLogoRemoved,
  setCompanyLogoToUpload,
  onOpenAliases,
}: Props) => {
  const handleFieldChangeWithValidation = (
    fieldName: CompanyFieldsNames,
    value: string,
  ) => {
    const key = fieldName as keyof IFieldsErrors;
    if (fieldsErrors?.[key]) {
      setFieldsErrors(prev => {
        const updatedErrors = { ...prev };
        delete updatedErrors[key];
        return updatedErrors;
      });
    }
    onChange(fieldName, value);
  };

  return (
    <>
      <FormControl
        label={translateText(`${I18N_COMPANY_LABEL_PATH}.companyPhoto`)}
        wrapperClassName={formsStyles['form-row']}
      >
        <CompanyLogo
          logoUrl={company.logoUrl}
          setCompanyLogoToUpload={setCompanyLogoToUpload}
          setCompanyLogoRemoved={setCompanyLogoRemoved}
        />
      </FormControl>
      <FormControl
        label={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.companyName`)}
        required
        wrapperClassName={formsStyles['form-row']}
      >
        <TextInput
          borderBox
          fullWidth
          id="company-name-field"
          isRequired
          onChange={value => onChange(CompanyFieldsNames.NAME, value)}
          placeholder={translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.enterCompanyName`,
          )}
          value={company.name}
        />
      </FormControl>

      <>
        <FormControl
          label={translateText(
            `${I18N_PLATFORM_COMMON_WORD_PATH}.operatingName`,
          )}
          wrapperClassName={formsStyles['form-row']}
        >
          <TextInput
            borderBox
            fullWidth
            id="company-operatingName-field"
            isInvalid={!!fieldsErrors?.operatingName}
            onClear={() => onChange(CompanyFieldsNames.OPERATING_NAME, '')}
            onChange={value =>
              handleFieldChangeWithValidation(
                CompanyFieldsNames.OPERATING_NAME,
                value,
              )
            }
            placeholder={translateText(
              `${I18N_PLATFORM_COMMON_WORD_PATH}.enterCompanyOperatingName`,
            )}
            value={company.operatingName}
          />
        </FormControl>

        <FormControl
          label={translateText(`${I18N_PLATFORM_COMMON_WORD_PATH}.legalName`)}
          wrapperClassName={formsStyles['form-row']}
        >
          <TextInput
            borderBox
            fullWidth
            id="company-legalName-field"
            isInvalid={!!fieldsErrors?.legalName}
            onClear={() => onChange(CompanyFieldsNames.LEGAL_NAME, '')}
            onChange={value =>
              handleFieldChangeWithValidation(
                CompanyFieldsNames.LEGAL_NAME,
                value,
              )
            }
            placeholder={translateText(
              `${I18N_PLATFORM_COMMON_WORD_PATH}.enterCompanyLegalName`,
            )}
            value={company.legalName}
          />
        </FormControl>
      </>

      {onOpenAliases && showCompanyAliasesButton && (
        <div className={styles['company-aliases-button']}>
          <Button
            type="ghost"
            label={translateText(
              `${I18N_PLATFORM_ROLES_COMPANY_LABEL_PATH}.companyAliases`,
            )}
            iconPosition="right"
            icon="aliases"
            onClick={onOpenAliases}
          />
        </div>
      )}
    </>
  );
};
